import type { CHIEvent } from "@/lib/eventsData";
import { hasEventEnded } from "@shared/eventTiming";
import { matchesCalendarCostFilter, type CalendarCostFilter } from "./calendarPricing";

export type CalendarEventTypeFilter = "all" | CHIEvent["type"];

export type CalendarEventFilters = {
  type: CalendarEventTypeFilter;
  location: string;
  speaker: string;
  cost: CalendarCostFilter;
};

export const DEFAULT_CALENDAR_EVENT_FILTERS: CalendarEventFilters = {
  type: "all",
  location: "all",
  speaker: "all",
  cost: "all",
};

export function filterCalendarEvents(
  events: readonly CHIEvent[],
  filters: CalendarEventFilters,
): CHIEvent[] {
  return events.filter((event) => (
    (filters.type === "all" || event.type === filters.type)
    && (filters.location === "all" || event.location === filters.location)
    && (filters.speaker === "all" || event.speaker === filters.speaker)
    && matchesCalendarCostFilter(event, filters.cost)
  ));
}

export function hasActiveCalendarEventFilters(filters: CalendarEventFilters): boolean {
  return filters.type !== "all"
    || filters.location !== "all"
    || filters.speaker !== "all"
    || filters.cost !== "all";
}

export function resetCalendarEventFilters(): CalendarEventFilters {
  return { ...DEFAULT_CALENDAR_EVENT_FILTERS };
}

export function getCalendarLocationOptions(events: readonly CHIEvent[]): string[] {
  return Array.from(new Set(events.map((event) => event.location)))
    .sort((left, right) => left.localeCompare(right));
}

export function getCalendarSpeakerOptions(events: readonly CHIEvent[]): CHIEvent[] {
  return Array.from(new Map(events.map((event) => [event.speaker, event])).values())
    .sort((left, right) => left.speaker.localeCompare(right.speaker));
}

// ── FREE-TEXT SEARCH ─────────────────────────────────────────────────────────

/**
 * Whether an event answers a typed query.
 *
 * Matches across the fields someone actually remembers an event by — its title,
 * who is speaking, where, and the subtitle that usually names the partner. An
 * empty or whitespace query matches everything, so the search box starts open
 * rather than filtering to nothing.
 */
export function matchesCalendarSearch(event: CHIEvent, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;

  return [event.title, event.subtitle, event.speaker, event.speakerOrg, event.location, event.category]
    .filter(Boolean)
    .some((field) => field.toLowerCase().includes(needle));
}

// ── MONTH ────────────────────────────────────────────────────────────────────

export type CalendarMonthOption = { value: string; label: string };

/** The sentinel for "don't narrow by month". */
export const ALL_MONTHS = "all";

/**
 * The months that actually contain events, as `YYYY-MM` values with readable
 * labels.
 *
 * Derived from the data rather than listing twelve months, so the dropdown never
 * offers a month with nothing in it. Labels carry the year because this list spans
 * one, and "August" alone would be ambiguous by December.
 */
export function getCalendarMonthOptions(events: readonly CHIEvent[]): CalendarMonthOption[] {
  const months = new Map<string, string>();

  for (const event of events) {
    const value = event.date.slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(value) || months.has(value)) continue;

    const [year, month] = value.split("-");
    // Day 15 rather than 01: parsing a bare date as UTC and formatting it locally
    // can land on the previous month, which would mislabel the option.
    const label = new Date(Number(year), Number(month) - 1, 15)
      .toLocaleDateString("en-US", { month: "long", year: "numeric" });
    months.set(value, label);
  }

  return Array.from(months, ([value, label]) => ({ value, label }))
    .sort((left, right) => left.value.localeCompare(right.value));
}

export function matchesCalendarMonth(event: CHIEvent, month: string): boolean {
  return month === ALL_MONTHS || event.date.startsWith(month);
}

// ── UPCOMING VS PAST ─────────────────────────────────────────────────────────

/**
 * Whether an event has already happened.
 *
 * Date strings remain supported for date-only callers and tests. Live surfaces pass
 * a Date so events move after their end time in the event's advertised timezone.
 */
export function isPastCalendarEvent(event: CHIEvent, now: Date | string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(event.date)) return false;
  if (typeof now === "string") return event.date < now;
  return hasEventEnded(event, now);
}

/** Today as `YYYY-MM-DD` in the visitor's own timezone. */
export function calendarToday(now: Date = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
